import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './SubTotalSection.scss';
import { formatAmountWithLocale } from '../helper/common.helper';

interface SubTotalSectionProps {
  subTotal: number;
  pensionPay: number;
  holidayPay: number;
  grandTotal: number;
  currency?: string;
  onPensionChange: (value: number) => void;
  onHolidayChange: (value: number) => void;
  t: (key: string) => string;
  isPensionEnabled: boolean;
  isHolidayEnabled: boolean;
  pensionOptions?: number[];
  holidayOptions?: number[];
  calculatedSubAmount?: any;
  language?: string;
  vat?: number;
  vatAmount?: number;
  isCalculateInvoiceLoading?: boolean;
  friendUserID?: string;
  onShowCalculation?: () => void;
  isCalculationLoading?: boolean;
}

export const SubTotalSection: React.FC<SubTotalSectionProps> = ({
  subTotal,
  pensionPay,
  holidayPay,
  grandTotal,
  currency = 'Kr',
  onPensionChange,
  onHolidayChange,
  t,
  isPensionEnabled,
  isHolidayEnabled,
  pensionOptions = [1000, 1500, 2000, 2500, 3000],
  holidayOptions = [12, 20, 30, 40, 50],
  calculatedSubAmount,
  language = 'en',
  vat = 25,
  vatAmount = 0,
  isCalculateInvoiceLoading = false,
  friendUserID,
  onShowCalculation,
  isCalculationLoading = false,
}) => {
  const { i18n } = useTranslation();
  const vatPercentage = vat;
  const [isPensionDropdownOpen, setIsPensionDropdownOpen] = useState(false);
  const [isHolidayDropdownOpen, setIsHolidayDropdownOpen] = useState(false);

  // Get freelancer-specific data
  const freelancerData = calculatedSubAmount?.response?.freelancersData?.find(
    (fd: any) => fd.createdBy?.toString() === friendUserID?.toString()
  );

  // Get currency from API response or fall back to prop
  const displayCurrency = freelancerData?.currencyName || calculatedSubAmount?.response?.totalCalculation?.currencyName || currency || 'SEK';

  return (
    <div className="subtotal-wrapper">
    <div className="subtotal-section">
      <div className="subtotal-row">
        <span className="subtotal-label">{t('SUB_TOTAL') || 'Sub Total'} :</span>
        <div className="total-values">
          <div className="subtotal-value">
            {isCalculateInvoiceLoading ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              formatAmountWithLocale(
                freelancerData?.amount || 0,
                displayCurrency,
                i18n.language
              )
            )}
          </div>
          <div className="vat-row">
            <span className="vat-value">
              +{vatPercentage}% {t('VAT_LABEL') || 'VAT'} ({freelancerData?.vat > 0 ? formatAmountWithLocale(freelancerData?.vat, displayCurrency, i18n.language) : '0'})
            </span>
          </div>
        </div>
      </div>

      {isPensionEnabled && (
        <div className="input-row">
          <label className="input-label">{t('PENSION_PAY') || 'Pension Pay'} :</label>
          <div className="input-wrapper" onClick={() => setIsPensionDropdownOpen(!isPensionDropdownOpen)}>
            <span className="amount-input">{pensionPay || pensionOptions[0]}</span>
            <span className="currency-select">SEK</span>
            <svg className="dropdown-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="currentColor"
                strokeWidth="1.67"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {isPensionDropdownOpen && (
              <div className="dropdown-menu-custom">
                {pensionOptions.map((value) => (
                  <div
                    key={value}
                    className="dropdown-item-custom"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPensionChange(value);
                      setIsPensionDropdownOpen(false);
                    }}
                  >
                    {value} SEK
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {isHolidayEnabled && (
        <div className="input-row">
          <label className="input-label">{t('HOLIDAY_PAY') || 'Holiday Pay'} :</label>
          <div className="input-wrapper" onClick={() => setIsHolidayDropdownOpen(!isHolidayDropdownOpen)}>
            <span className="amount-input">{holidayPay || holidayOptions[0]}</span>
            <span className="currency-select">%</span>
            <svg className="dropdown-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="currentColor"
                strokeWidth="1.67"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {isHolidayDropdownOpen && (
              <div className="dropdown-menu-custom">
                {holidayOptions.map((value) => (
                  <div
                    key={value}
                    className="dropdown-item-custom"
                    onClick={(e) => {
                      e.stopPropagation();
                      onHolidayChange(value);
                      setIsHolidayDropdownOpen(false);
                    }}
                  >
                    {value}%
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grand-total-row">
        <span className="grand-total-label">{t('GRAND_TOTAL') || 'Grand Total'} :</span>
        <span className="grand-total-value">
          {isCalculateInvoiceLoading ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          ) : (
            formatAmountWithLocale(
              freelancerData?.amountIncludingVat || 0,
              displayCurrency,
              i18n.language
            )
          )}
        </span>
      </div>

      <button 
        className="calculation-link" 
        onClick={onShowCalculation}
        disabled={!onShowCalculation || isCalculationLoading || isCalculateInvoiceLoading}
      >
        {isCalculationLoading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            {t('LOADING') || 'Loading...'}
          </>
        ) : (
          <>
            {t('SEE_HOW_WE_CALCULATED') || 'See how we calculated'}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M6 12L10 8L6 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </>
        )}
      </button>
    </div>
    </div>
  );
};
